import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AdminConfigService } from '@/services';
import { updateConfigStorageReference } from '@/utils/config';
import { AdminConfigConverter } from '@/converters/admin-config';
import { ApplicationComponent, ApplicationComponentId } from '@/model/application-component';
import { PutAppMenuButtonRequest, PutConfigCardRequest, PutHomeAreaFeaturesRequest } from '../request/admin-config';
import { GetParamsObject } from '@/model/query-params';

export const useGetConfig = () => {
  const queryClient = useQueryClient();
  const queryKey = ['GET_CONFIG'];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await AdminConfigService.config();
      if (err) {
        throw err;
      }

      if (data && data.client_id) {
        updateConfigStorageReference(data);
      }

      return data;
    },
    refetchOnWindowFocus: true,
  });

  const forceRefetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient],
  );

  return { ...query, forceRefetch };
};

export const useGetConfigSMTP = (enabled = true) => {
  const queryClient = useQueryClient();
  const queryKey = ['GET_CONFIG_SMTP'];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await AdminConfigService.getSMTP();
      if (err) {
        throw err;
      }
      return AdminConfigConverter.toConfigSMTPFromByGetSMTPResponse(data);
    },
    refetchOnWindowFocus: false,
    enabled,
  });

  const forceRefetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient],
  );

  return { ...query, forceRefetch };
};

export const usePutConfigSMTPMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ADMIN_CONFIG_SMTP'],
    mutationFn: async ({ payload }: any) => {
      const [err] = await AdminConfigService.putSMTP(payload);
      if (err) {
        throw err;
      }
    },
  });
};

export const useGetConfigCard = () => {
  const queryClient = useQueryClient();
  const queryKey = ['GET_CONFIG_CARD'];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await AdminConfigService.getCard();
      if (err) {
        throw err;
      }
      return AdminConfigConverter.toConfigCardFromGetCardResponse(data);
    },
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient],
  );

  return { ...query, forceRefetch };
};

export const usePutConfigCardMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ADMIN_CONFIG_CARD'],
    mutationFn: async (payload: PutConfigCardRequest) => {
      const [err] = await AdminConfigService.putCard(payload);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useAppApplicationComponentArea = (componentId?: ApplicationComponentId, parentId?: ApplicationComponentId) => {
  const queryClient = useQueryClient();
  const queryKey = ['GET_ADMIN_APPLICATION_COMPONENT', componentId, parentId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await AdminConfigService.getAdminApplicationComponent();
      if (err) {
        throw err;
      }
      const all = AdminConfigConverter.toDomainFromGetAdminApplicationComponentResponse(data);
      const isActive = (() => {
        if (parentId) {
          return all.some(f => f.parent_id === parentId && f.active);
        }
        if (componentId) {
          const component = all.find(f => f.component_id === componentId);
          return component?.active ?? false;
        }
        return false;
      })();

      return { is_active: isActive };
    },
    refetchOnWindowFocus: false,
    enabled: !!componentId || !!parentId,
  });

  const forceRefetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, componentId, parentId],
  );

  return { ...query, forceRefetch };
};

export const useAppApplicationComponentAreaItem = (componentId?: ApplicationComponentId) => {
  const queryClient = useQueryClient();
  const queryKey = ['GET_ADMIN_APPLICATION_COMPONENT_ITEM', componentId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await AdminConfigService.getAdminApplicationComponent();
      if (err) {
        throw err;
      }
      const all = AdminConfigConverter.toDomainFromGetAdminApplicationComponentResponse(data);

      return all.find(f => f.component_id === componentId);
    },
    refetchOnWindowFocus: false,
    enabled: !!componentId,
  });

  const forceRefetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, componentId],
  );

  return { ...query, forceRefetch };
};

export const useEnableApplicationComponentMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ADMIN_APPLICATION_COMPONENT'],
    mutationFn: async (params: ApplicationComponent[]) => {
      const promises = params.map(({ component_id, active }) =>
        AdminConfigService.putAdminApplicationComponent({ component_id, active }),
      );
      const results = await Promise.all(promises.map(p => p.catch(err => ({ error: err }))));
      const errors = results.filter(result => 'error' in result);
      if (errors.length > 0) {
        throw new Error(`Failed to update ${errors.length} areas.`);
      }
      return results;
    },
  });
};

export const useGetHomeAreaFeatures = (teamUUID: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['GET_HOME_AREA_FEATURES', teamUUID];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await AdminConfigService.getHomeAreaFeatures(GetParamsObject({ team_uuid: teamUUID }));
      if (err) {
        throw err;
      }
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, teamUUID],
  );

  return { ...query, forceRefetch };
};

export const useGetAppMenuButtons = (teamUUID: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['GET_APP_MENU_BUTTONS', teamUUID];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await AdminConfigService.getAppMenuButtons(GetParamsObject({ team_uuid: teamUUID }));
      if (err) {
        throw err;
      }
      return AdminConfigConverter.toDomainAppMenuButton(data);
    },
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, teamUUID],
  );

  return { ...query, forceRefetch };
};

export const usePutHomeAreaFeatures = () => {
  return useMutation({
    mutationKey: ['PUT_HOME_AREA_FEATURES'],
    mutationFn: async (payload: PutHomeAreaFeaturesRequest) => {
      const [err] = await AdminConfigService.putHomeAreaFeatures(payload);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const usePutAppMenuButtons = () => {
  return useMutation({
    mutationKey: ['PUT_APP_MENU_BUTTONS'],
    mutationFn: async (payload: PutAppMenuButtonRequest) => {
      const [err] = await AdminConfigService.putAppMenuButtons(payload);
      if (err) {
        throw err;
      }
      return;
    },
  });
};
